import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchLeaderboard, mergeDrivers, rebuildRankings } from '../auth/api';
import { useAuth } from '../auth/AuthContext';
import { formatLapSeconds } from '../analytics/lapTime';
import {
  compositeScores,
  CompositeWeights,
  DEFAULT_WEIGHTS,
  formatPaceIndex,
  LAP_TIME_METRICS,
  RANK_METRICS,
  RankMetric,
  sortByMetric,
} from '../analytics/ranking';
import { Leaderboard, LeaderboardRow } from '../types';
import { consistencyLabel } from './EventStats';
import { navigate } from './EventsList';
import { SessionBadge } from './SessionBadge';
import './EventsList.css';
import './EventStats.css';
import './RankingsPage.css';

const METRIC_KEY = 'karting-rankings-metric';
const WEIGHTS_KEY = 'karting-rankings-weights';
const TRACK_KEY = 'karting-rankings-track';
const MIN_LAPS_KEY = 'karting-rankings-min-laps';
const DEFAULT_MIN_LAPS = 20;

const WEIGHT_LABELS: { key: keyof CompositeWeights; label: string }[] = [
  { key: 'pace', label: 'Pace' },
  { key: 'rating', label: 'Rating' },
  { key: 'consistency', label: 'Consistency' },
  { key: 'experience', label: 'Experience' },
];

function readStored<T>(key: string, parse: (raw: string) => T | null, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return parse(raw) ?? fallback;
  } catch {
    return fallback;
  }
}

function store(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Private mode or full storage: the setting just is not remembered.
  }
}

function parseMetric(raw: string): RankMetric | null {
  return RANK_METRICS.some((m) => m.value === raw) ? (raw as RankMetric) : null;
}

function parseWeights(raw: string): CompositeWeights | null {
  try {
    const parsed = JSON.parse(raw) as Partial<CompositeWeights>;
    const out = { ...DEFAULT_WEIGHTS };
    for (const { key } of WEIGHT_LABELS) {
      const v = parsed[key];
      if (typeof v === 'number' && v >= 0 && v <= 100) out[key] = v;
    }
    return out;
  } catch {
    return null;
  }
}

function formatSeconds(seconds: number | null): string {
  return seconds === null ? '—' : formatLapSeconds(seconds);
}

type Busy = null | 'merge' | 'rebuild';

/**
 * All-time driver leaderboard. Every metric is stored per driver per event;
 * which one ranks the list is the viewer's choice and is remembered per device.
 */
export const RankingsPage: React.FC = () => {
  const { isAdmin } = useAuth();

  const [metric, setMetric] = useState<RankMetric>(() =>
    readStored(METRIC_KEY, parseMetric, 'composite'),
  );
  const [weights, setWeights] = useState<CompositeWeights>(() =>
    readStored(WEIGHTS_KEY, parseWeights, DEFAULT_WEIGHTS),
  );
  // undefined: not chosen yet, start on the most recent track once known.
  // '': all tracks.
  const [track, setTrack] = useState<string | undefined>(() =>
    readStored<string | undefined>(TRACK_KEY, (raw) => raw, undefined),
  );
  const [year, setYear] = useState<number | null>(null);
  const [minLaps, setMinLaps] = useState<number>(() =>
    readStored(MIN_LAPS_KEY, (raw) => {
      const n = Number.parseInt(raw, 10);
      return Number.isFinite(n) && n >= 1 ? n : null;
    }, DEFAULT_MIN_LAPS),
  );
  const [minLapsDraft, setMinLapsDraft] = useState(String(minLaps));
  const [search, setSearch] = useState('');

  const [data, setData] = useState<Leaderboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [merging, setMerging] = useState(false);
  const [mergeSource, setMergeSource] = useState<LeaderboardRow | null>(null);
  const [mergeTarget, setMergeTarget] = useState<LeaderboardRow | null>(null);
  const [confirmRebuild, setConfirmRebuild] = useState(false);
  const [busy, setBusy] = useState<Busy>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => store(METRIC_KEY, metric), [metric]);
  useEffect(() => store(WEIGHTS_KEY, JSON.stringify(weights)), [weights]);
  useEffect(() => {
    if (track !== undefined) store(TRACK_KEY, track);
  }, [track]);
  useEffect(() => store(MIN_LAPS_KEY, String(minLaps)), [minLaps]);

  // Typing "150" should not fire three requests.
  useEffect(() => {
    const n = Number.parseInt(minLapsDraft, 10);
    if (!Number.isFinite(n) || n < 1 || n === minLaps) return;
    const timer = setTimeout(() => setMinLaps(n), 400);
    return () => clearTimeout(timer);
  }, [minLapsDraft, minLaps]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchLeaderboard({ track: track || null, year, minLaps })
      .then((board) => {
        if (cancelled) return;
        setData(board);
        // First visit, or a remembered track that no longer has events.
        if (track === undefined || (track && !board.tracks.includes(track))) {
          setTrack(board.defaultTrack ?? '');
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load rankings');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [track, year, minLaps, reloadKey]);

  const lapTimeMetric = LAP_TIME_METRICS.has(metric);
  const fallbackTrack = data?.defaultTrack ?? data?.tracks[0] ?? '';

  const chooseMetric = useCallback(
    (next: RankMetric) => {
      setMetric(next);
      // Seconds at one circuit say nothing about another.
      if (LAP_TIME_METRICS.has(next) && !track && fallbackTrack) setTrack(fallbackTrack);
    },
    [track, fallbackTrack],
  );

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const composite = useMemo(() => compositeScores(rows, weights), [rows, weights]);
  const ranked = useMemo(() => sortByMetric(rows, metric, composite), [rows, metric, composite]);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ranked
      .map((row, i) => ({ row, rank: i + 1 }))
      .filter(({ row }) => !q || row.displayName.toLowerCase().includes(q));
  }, [ranked, search]);

  const metricInfo = RANK_METRICS.find((m) => m.value === metric)!;

  const stopMerging = (): void => {
    setMerging(false);
    setMergeSource(null);
    setMergeTarget(null);
  };

  const onRowClick = (row: LeaderboardRow): void => {
    if (!merging) {
      navigate(`/rankings/${row.driverId}`);
      return;
    }
    if (!mergeSource) {
      setMergeSource(row);
    } else if (row.driverId === mergeSource.driverId) {
      setMergeSource(null);
    } else {
      setMergeTarget(row);
    }
  };

  const confirmMerge = async (): Promise<void> => {
    if (!mergeSource || !mergeTarget) return;
    setBusy('merge');
    setActionError(null);
    try {
      await mergeDrivers(mergeSource.driverId, mergeTarget.driverId);
      setNotice(`Merged ${mergeSource.displayName} into ${mergeTarget.displayName}.`);
      stopMerging();
      setReloadKey((k) => k + 1);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not merge');
    } finally {
      setBusy(null);
    }
  };

  const runRebuild = async (): Promise<void> => {
    setBusy('rebuild');
    setActionError(null);
    try {
      const { rankedEvents } = await rebuildRankings();
      setNotice(`Rankings rebuilt from ${rankedEvents} event${rankedEvents === 1 ? '' : 's'}.`);
      setConfirmRebuild(false);
      setReloadKey((k) => k + 1);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not rebuild');
    } finally {
      setBusy(null);
    }
  };

  const header = (key: RankMetric, label: string, title: string): React.ReactNode => (
    <th className={`num rk-sortable${metric === key ? ' rk-sorted' : ''}`} title={title}>
      <button type="button" onClick={() => chooseMetric(key)}>
        {label}
      </button>
    </th>
  );

  return (
    <div className="App">
      <div className="app-header">
        <h1>Karting Live Timing</h1>
        <SessionBadge />
      </div>
      <div className="app-main">
        <div className="main-content">
          <div className="events-page rk-page">
            <div className="events-toolbar">
              <h2 className="events-title">Driver rankings</h2>
              <div className="rk-toolbar-actions">
                {isAdmin && (
                  <>
                    <button
                      className={`events-link-button${merging ? ' rk-active' : ''}`}
                      onClick={() => (merging ? stopMerging() : setMerging(true))}
                      disabled={busy !== null}
                      title="Two names for the same person: fold one into the other"
                    >
                      {merging ? 'Cancel merge' : 'Merge drivers'}
                    </button>
                    <button
                      className="events-link-button"
                      onClick={() => setConfirmRebuild(true)}
                      disabled={busy !== null}
                      title="Recompute every driver from every recorded event"
                    >
                      Rebuild rankings
                    </button>
                  </>
                )}
                <button className="events-link-button" onClick={() => navigate('/')}>
                  ← Back to live
                </button>
              </div>
            </div>

            <div className="rk-controls">
              <label>
                <span>Rank by</span>
                <select
                  aria-label="Rank by"
                  value={metric}
                  onChange={(e) => chooseMetric(e.target.value as RankMetric)}
                >
                  {RANK_METRICS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Track</span>
                <select
                  aria-label="Track"
                  value={track ?? ''}
                  onChange={(e) => setTrack(e.target.value)}
                >
                  <option value="" disabled={lapTimeMetric}>
                    All tracks
                  </option>
                  {(data?.tracks ?? (track ? [track] : [])).map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Season</span>
                <select
                  aria-label="Season"
                  value={year ?? ''}
                  onChange={(e) => setYear(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">All time</option>
                  {(data?.years ?? []).map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Min laps</span>
                <input
                  aria-label="Min laps"
                  type="number"
                  min={1}
                  inputMode="numeric"
                  value={minLapsDraft}
                  onChange={(e) => setMinLapsDraft(e.target.value)}
                />
              </label>
              <label className="rk-search">
                <span>Find</span>
                <input
                  aria-label="Find driver"
                  type="search"
                  placeholder="Driver name"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </label>
            </div>

            <p className="rk-hint">
              {metricInfo.hint}
              {lapTimeMetric && !track && ' Pick a track to compare lap times.'}
              {' '}A driver qualifies for an event with {data?.qualifyingCleanLaps ?? 10}+ clean
              laps; rating is all-time whatever the filters.
            </p>

            {metric === 'composite' && (
              <div className="rk-weights" aria-label="Composite weights">
                {WEIGHT_LABELS.map(({ key, label }) => (
                  <label key={key}>
                    <span>
                      {label} <strong>{weights[key]}</strong>
                    </span>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      aria-label={`${label} weight`}
                      value={weights[key]}
                      onChange={(e) => setWeights({ ...weights, [key]: Number(e.target.value) })}
                    />
                  </label>
                ))}
                <button
                  className="events-link-button"
                  onClick={() => setWeights(DEFAULT_WEIGHTS)}
                  type="button"
                >
                  Reset weights
                </button>
              </div>
            )}

            {notice && <div className="events-notice">{notice}</div>}
            {actionError && <div className="events-error">Error: {actionError}</div>}

            {confirmRebuild && (
              <div className="events-confirm rk-confirm">
                <p>
                  Recompute every driver from all recorded events? Ratings are replayed
                  in date order. Merges are kept.
                </p>
                <div className="events-confirm-actions">
                  <button
                    className="events-link-button"
                    onClick={() => void runRebuild()}
                    disabled={busy !== null}
                  >
                    {busy === 'rebuild' ? 'Rebuilding…' : 'Yes, rebuild'}
                  </button>
                  <button
                    className="events-link-button"
                    onClick={() => setConfirmRebuild(false)}
                    disabled={busy !== null}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {merging && !mergeTarget && (
              <div className="rk-merge-banner">
                {mergeSource
                  ? `Now pick the driver to keep. ${mergeSource.displayName} will be merged into them.`
                  : 'Pick the driver whose name should be merged away.'}
              </div>
            )}

            {mergeSource && mergeTarget && (
              <div className="events-confirm rk-confirm">
                <p>
                  Merge <strong>{mergeSource.displayName}</strong> into{' '}
                  <strong>{mergeTarget.displayName}</strong>? Their names become aliases of{' '}
                  {mergeTarget.displayName} and ratings are recomputed. You can split a name
                  off again from the driver's page.
                </p>
                <div className="events-confirm-actions">
                  <button
                    className="events-link-button"
                    onClick={() => void confirmMerge()}
                    disabled={busy !== null}
                  >
                    {busy === 'merge' ? 'Merging…' : 'Yes, merge'}
                  </button>
                  <button
                    className="events-link-button"
                    onClick={() => setMergeTarget(null)}
                    disabled={busy !== null}
                  >
                    Back
                  </button>
                </div>
              </div>
            )}

            {loading && !data && <div className="events-loading">Loading rankings…</div>}
            {error && <div className="events-error">Error: {error}</div>}

            {data && !error && (
              visible.length === 0 ? (
                <div className="events-empty">
                  {rows.length === 0
                    ? data.rankedEvents === 0
                      ? 'No recorded events to rank yet.'
                      : 'No drivers match these filters. Try a lower minimum or another track.'
                    : 'No driver by that name.'}
                </div>
              ) : (
                <div className={`events-table-wrapper${loading ? ' rk-stale' : ''}`}>
                  <table className="events-table rk-table">
                    <thead>
                      <tr>
                        <th className="num">#</th>
                        <th>Driver</th>
                        <th className="num">Events</th>
                        <th className="num">Laps</th>
                        {header('best', 'Best', 'Fastest lap')}
                        {header('cleanAvg', 'Clean avg', 'Mean of laps within 5% of the median')}
                        {header('paceIndex', 'Pace', 'Gap to the fastest driver in the same event')}
                        <th>Consistency</th>
                        {header('rating', 'Rating', 'All-time Elo-style rating')}
                        {header('composite', 'Score', 'Composite score, 0–100')}
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map(({ row, rank }) => {
                        const c = consistencyLabel(row.consistencyCv);
                        const score = composite.get(row.driverId);
                        const isSource = mergeSource?.driverId === row.driverId;
                        return (
                          <tr
                            key={row.driverId}
                            className={`events-row-clickable${isSource ? ' rk-row--source' : ''}`}
                            onClick={() => onRowClick(row)}
                          >
                            <td className="num rk-rank">{rank}</td>
                            <td className="events-title-cell">{row.displayName}</td>
                            <td className="num">{row.events}</td>
                            <td className="num">{row.laps.toLocaleString()}</td>
                            <td className="num">{formatSeconds(row.bestSeconds)}</td>
                            <td className="num">{formatSeconds(row.cleanAverageSeconds)}</td>
                            <td className="num">{formatPaceIndex(row.paceIndex)}</td>
                            <td className={c.cls}>{c.text}</td>
                            <td className="num">
                              {row.ratedEvents > 0 ? Math.round(row.rating) : '—'}
                            </td>
                            <td className="num">
                              {score === null || score === undefined ? '—' : score.toFixed(1)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RankingsPage;
